import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { BarChart3, ChevronRight, TrendingDown, TrendingUp } from 'lucide-react';
import {
  benchmarkSummary,
  computeCategoryBenchmark,
  formatPercentileHeadline,
  formatVsAverage,
  getMetricComparisons,
  type BenchmarkMetric,
} from '../utils/categoryBenchmark';
import { createCategorySlug } from '../utils/urlHelpers';
import type { ForumBase } from '../types/forum';

interface Props {
  forum: ForumBase;
  allForums: ForumBase[];
  darkMode: boolean;
}

const METRIC_TABS: { key: BenchmarkMetric; label: string }[] = [
  { key: 'members', label: 'Üye' },
  { key: 'posts', label: 'İleti' },
  { key: 'score', label: 'Skor' },
];

const CategoryBenchmark: React.FC<Props> = ({ forum, allForums, darkMode }) => {
  const [activeMetric, setActiveMetric] = useState<BenchmarkMetric>('members');

  const benchmark = useMemo(
    () => computeCategoryBenchmark(forum, allForums),
    [forum, allForums]
  );

  if (!benchmark) return null;

  const comparisons = getMetricComparisons(benchmark);
  const active =
    comparisons.find((c) => c.key === activeMetric) ?? comparisons[0];
  const activeRank =
    activeMetric === 'members'
      ? benchmark.memberRank
      : activeMetric === 'posts'
        ? benchmark.postsRank
        : benchmark.scoreRank;
  const activePercentile =
    activeMetric === 'members'
      ? benchmark.memberPercentile
      : activeMetric === 'posts'
        ? benchmark.postsPercentile
        : benchmark.scorePercentile;

  const card = darkMode
    ? 'bg-gradient-to-br from-gray-800 to-gray-900 border-gray-700'
    : 'bg-gradient-to-br from-indigo-50 to-white border-indigo-100';
  const title = darkMode ? 'text-white' : 'text-indigo-950';
  const muted = darkMode ? 'text-gray-400' : 'text-gray-600';
  const statBg = darkMode ? 'bg-gray-900/60 border-gray-600' : 'bg-white/90 border-indigo-100';

  return (
    <section
      className={`rounded-xl border p-4 sm:p-5 h-full flex flex-col ${card}`}
      aria-labelledby="category-benchmark-heading"
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0">
          <h3
            id="category-benchmark-heading"
            className={`flex items-center gap-2 font-semibold text-base sm:text-lg ${title}`}
          >
            <BarChart3 size={20} className="text-indigo-500 shrink-0" />
            Kategori kıyası
          </h3>
          <p className={`text-sm mt-1 leading-relaxed ${muted}`}>
            <strong className={darkMode ? 'text-gray-200' : 'text-gray-800'}>{forum.category}</strong>{' '}
            kategorisindeki <strong className={darkMode ? 'text-gray-200' : 'text-gray-800'}>{benchmark.peerCount}</strong>{' '}
            forum arasında bu sitenin konumu — rakiplerle kıyas için.
          </p>
        </div>
        <Link
          to={`/category/${createCategorySlug(forum.category)}`}
          className={`shrink-0 inline-flex items-center gap-0.5 text-xs font-medium px-2.5 py-1.5 rounded-lg border ${
            darkMode
              ? 'border-indigo-500/40 text-indigo-300 hover:bg-indigo-950/50'
              : 'border-indigo-200 text-indigo-700 hover:bg-indigo-50'
          }`}
        >
          Kategori
          <ChevronRight size={14} />
        </Link>
      </div>

      <div
        className={`rounded-xl border p-3 sm:p-4 mb-4 ${statBg}`}
        role="tabpanel"
        aria-label={`${active.label} kıyası`}
      >
        <div className="flex flex-wrap gap-2 mb-3">
          {METRIC_TABS.map(({ key, label }) => (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={activeMetric === key}
              onClick={() => setActiveMetric(key)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                activeMetric === key
                  ? darkMode
                    ? 'bg-indigo-600 text-white'
                    : 'bg-indigo-600 text-white'
                  : darkMode
                    ? 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        <p className={`text-sm font-medium ${darkMode ? 'text-indigo-200' : 'text-indigo-800'}`}>
          {formatPercentileHeadline(activePercentile, activeRank, benchmark.peerCount)}
        </p>
        <p className={`text-xs mt-1 ${muted}`}>
          {benchmarkSummary(benchmark, activeMetric).split('(')[1]?.replace(')', '') ?? ''} ·{' '}
          {formatVsAverage(active.vsAvgPercent)} kategori ortalamasına göre
        </p>

        <div className="mt-3 grid grid-cols-2 gap-3">
          <div>
            <div className={`text-[10px] uppercase tracking-wide font-bold ${muted}`}>Bu forum</div>
            <div className={`text-lg font-bold tabular-nums ${title}`}>
              {active.forumValue.toLocaleString('tr-TR')}
            </div>
          </div>
          <div>
            <div className={`text-[10px] uppercase tracking-wide font-bold ${muted}`}>Kategori ort.</div>
            <div className={`text-lg font-semibold tabular-nums ${muted}`}>
              {active.avgValue.toLocaleString('tr-TR')}
            </div>
          </div>
        </div>

        <div className="mt-3">
          <div className={`flex justify-between text-[10px] mb-1 ${muted}`}>
            <span>Kategori içi konum</span>
            <span>
              #{active.rank} / {active.total}
            </span>
          </div>
          <div
            className={`h-2 rounded-full overflow-hidden ${darkMode ? 'bg-gray-700' : 'bg-indigo-100'}`}
            aria-hidden
          >
            <div
              className="h-full rounded-full bg-indigo-500 transition-all duration-300"
              style={{
                width: `${Math.max(8, ((active.total - active.rank + 1) / active.total) * 100)}%`,
              }}
            />
          </div>
        </div>
      </div>

      <dl className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs flex-1">
        {comparisons.map((row) => {
          const diff = row.vsAvgPercent;
          const aboveAvg = diff !== null && diff > 0;
          const belowAvg = diff !== null && diff < 0;

          return (
            <div
              key={row.key}
              className={`rounded-lg border p-2.5 ${
                row.key === activeMetric
                  ? darkMode
                    ? 'border-indigo-500/50 bg-indigo-950/30'
                    : 'border-indigo-300 bg-indigo-50/80'
                  : statBg
              }`}
            >
              <dt className={`font-semibold ${muted}`}>{row.label}</dt>
              <dd className={`font-bold text-sm tabular-nums mt-0.5 ${title}`}>
                #{row.rank}
                <span className={`font-normal text-[11px] ml-1 ${muted}`}>/ {row.total}</span>
              </dd>
              <dd className={`mt-1 flex items-center gap-1 text-[11px] ${muted}`}>
                {aboveAvg && <TrendingUp size={12} className="text-green-500 shrink-0" />}
                {belowAvg && <TrendingDown size={12} className="text-red-500 shrink-0" />}
                <span>{formatVsAverage(diff)}</span>
              </dd>
            </div>
          );
        })}
      </dl>

      <p className={`text-[11px] mt-3 leading-relaxed ${muted}`}>
        Ortalamalar yalnızca aynı kategorideki aktif forumlardan hesaplanır. Sıra ve yüzde, kategori
        içindeki göreli büyüklüğü gösterir; ziyaretçiler için “bu forum kendi alanında ne kadar
        büyük?” sorusunun yanıtıdır.
      </p>
    </section>
  );
};

export default CategoryBenchmark;
